import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api, { trackActivity, researchReportsBulkAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { usePreview } from '../contexts/PreviewContext';
import InlineContentEditor from '../components/InlineContentEditor';
import ResearchReportContent from '../components/ResearchReportContent';
import BulkReportDownloadModal from '../components/BulkReportDownloadModal';
import './ResearchReportsApp.css';
import ManagedDisclaimer from '../components/ManagedDisclaimer';

const ResearchReportsApp = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { previewDomain, isPreviewMode } = usePreview();
  const location = useLocation();
  const [assetClasses, setAssetClasses] = useState([]);
  const [funds, setFunds] = useState([]);
  const [selectedAssetClass, setSelectedAssetClass] = useState('');
  const [selectedFund, setSelectedFund] = useState('');
  const [pendingApir, setPendingApir] = useState(null); // For handling navigation from screener
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [fundNameOverride, setFundNameOverride] = useState('');

  // Bulk download state
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [bulkJobId, setBulkJobId] = useState(null);
  const [bulkJobProgress, setBulkJobProgress] = useState(0);
  const [bulkJobStatus, setBulkJobStatus] = useState(null);
  const [bulkMessage, setBulkMessage] = useState(null);
  const bulkPollRef = useRef(null);

  // Track app access and fetch asset classes on mount
  useEffect(() => {
    trackActivity('app_access', 'research_reports');
    api.get('/api/reports/asset-classes')
      .then((response) => {
        setAssetClasses(response.data);
      })
      .catch((err) => {
        console.error('Error fetching asset classes:', err);
        setError('Failed to load asset classes');
      });
  }, []);

  // Handle navigation from Fund Screener
  useEffect(() => {
    if (location.state?.selectedAssetClass) {
      setSelectedAssetClass(location.state.selectedAssetClass);
      if (location.state.selectedApir) {
        setPendingApir(location.state.selectedApir);
      }
    }
  }, [location.state]);

  // Fetch funds when asset class changes
  useEffect(() => {
    if (selectedAssetClass) {
      setFunds([]);
      if (!pendingApir) {
        setSelectedFund('');
        setReportData(null);
      }

      api.get(`/api/reports/funds/${encodeURIComponent(selectedAssetClass)}`)
        .then((response) => {
          setFunds(response.data);
          // Auto-select fund if navigated from screener
          if (pendingApir) {
            const matchingFund = response.data.find(f => f.apir === pendingApir);
            if (matchingFund) {
              setSelectedFund(matchingFund.apir);
            }
            setPendingApir(null);
          }
        })
        .catch((err) => {
          console.error('Error fetching funds:', err);
          setError('Failed to load funds');
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetClass]);

  // Fetch report data when fund changes
  useEffect(() => {
    if (selectedFund) {
      setIsLoading(true);
      setError(null);
      setFundNameOverride(''); // Reset override when fund changes

      api.get(`/api/reports/fund/${selectedFund}`)
        .then((response) => {
          setReportData(response.data);
        })
        .catch((err) => {
          console.error('Error fetching report:', err);
          setError('Failed to load report data');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [selectedFund]);

  const handleDownloadPdf = async () => {
    if (!selectedFund) return;

    setIsPdfLoading(true);
    try {
      const params = {};
      if (fundNameOverride) {
        params.fund_name_override = fundNameOverride;
      }
      // Add preview domain if in preview mode
      if (isPreviewMode && previewDomain) {
        params.preview_domain = previewDomain;
      }

      const response = await api.get(`/api/reports/fund/${selectedFund}/pdf`, {
        responseType: 'blob',
        params
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${displayFundName || 'Report'}_Research_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError('Failed to download PDF');
    } finally {
      setIsPdfLoading(false);
    }
  };

  // Handle content save from inline editor - refresh report data
  const handleContentSave = async () => {
    if (selectedFund) {
      try {
        const response = await api.get(`/api/reports/fund/${selectedFund}`);
        setReportData(response.data);
      } catch (err) {
        console.error('Error refreshing report:', err);
      }
    }
  };

  // ---- Bulk Download Handlers ----

  useEffect(() => {
    return () => {
      if (bulkPollRef.current) clearInterval(bulkPollRef.current);
    };
  }, []);

  const pollBulkJobStatus = async (jobId) => {
    try {
      const status = await researchReportsBulkAPI.jobStatus(jobId);
      setBulkJobProgress(status.progress || 0);
      setBulkJobStatus(status.status);

      if (status.status === 'completed') {
        if (bulkPollRef.current) {
          clearInterval(bulkPollRef.current);
          bulkPollRef.current = null;
        }
        await downloadBulkResult(jobId, status.generated, status.failed);
      } else if (status.status === 'failed') {
        if (bulkPollRef.current) {
          clearInterval(bulkPollRef.current);
          bulkPollRef.current = null;
        }
        setIsBulkGenerating(false);
        setBulkJobId(null);
        setBulkMessage({
          type: 'error',
          text: `Generation failed: ${status.error || 'Unknown error'}`,
        });
      }
    } catch (err) {
      console.error('Error polling bulk job status:', err);
      if (err.response?.status === 404) {
        if (bulkPollRef.current) {
          clearInterval(bulkPollRef.current);
          bulkPollRef.current = null;
        }
        setIsBulkGenerating(false);
        setBulkJobId(null);
        setBulkMessage({
          type: 'error',
          text: 'Job lost — the server may have restarted during generation. Please try again.',
        });
      }
    }
  };

  const downloadBulkResult = async (jobId, generated, failed) => {
    try {
      const response = await researchReportsBulkAPI.download(jobId);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = `research_reports_${new Date().toISOString().split('T')[0]}.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?(?:;|$)/);
        if (match) filename = match[1];
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setIsBulkGenerating(false);
      setBulkJobId(null);
      setBulkJobProgress(0);

      if (failed > 0) {
        setBulkMessage({
          type: 'warning',
          text: `Generated ${generated} reports successfully. ${failed} failed.`,
        });
      } else {
        setBulkMessage({
          type: 'success',
          text: `Successfully generated ${generated} research reports! Download started.`,
        });
      }

    } catch (err) {
      console.error('Error downloading bulk result:', err);
      setIsBulkGenerating(false);
      setBulkJobId(null);
      setBulkMessage({ type: 'error', text: 'Failed to download reports' });
    }
  };

  const handleBulkGenerate = async (apirCodes) => {
    setIsBulkGenerating(true);
    setBulkJobProgress(0);
    setBulkJobStatus('pending');
    setBulkMessage({ type: 'info', text: `Starting generation for ${apirCodes.length} reports...` });

    try {
      const response = await researchReportsBulkAPI.generate(apirCodes);
      setBulkJobId(response.job_id);
      setBulkMessage({
        type: 'info',
        text: `Generating ${apirCodes.length} research reports in background. Progress will update automatically.`,
      });

      bulkPollRef.current = setInterval(() => {
        pollBulkJobStatus(response.job_id);
      }, 2000);
    } catch (err) {
      console.error('Error starting bulk generation:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to start generation';
      setBulkMessage({ type: 'error', text: errorMessage });
      setIsBulkGenerating(false);
    }
  };

  const handleOpenBulkDownload = () => {
    setBulkMessage(null);
    setIsBulkModalOpen(true);
  };

  const handleCloseBulkModal = () => {
    if (!isBulkGenerating) {
      setIsBulkModalOpen(false);
      setBulkMessage(null);
    }
  };

  // Get display fund name (override or original)
  const displayFundName = fundNameOverride || reportData?.fund_details?.fund_name;

  return (
    <div className="research-reports-container">
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Selector Controls */}
      <div className="selector-controls">
        <div className="selector-row">
          <div className="selector-item">
            <label className="selector-label">Asset Class</label>
            <select
              className="selector-dropdown"
              value={selectedAssetClass}
              onChange={(e) => setSelectedAssetClass(e.target.value)}
            >
              <option value="">Select Asset Class...</option>
              {assetClasses.map((ac) => (
                <option key={ac} value={ac}>{ac}</option>
              ))}
            </select>
          </div>
          <div className="selector-item">
            <label className="selector-label">Fund Name</label>
            <select
              className="selector-dropdown"
              value={selectedFund}
              onChange={(e) => setSelectedFund(e.target.value)}
              disabled={!selectedAssetClass}
            >
              <option value="">Select Fund...</option>
              {funds.map((fund) => (
                <option key={fund.apir} value={fund.apir}>{fund.name}</option>
              ))}
            </select>
          </div>
          <div className="selector-item selector-button">
            <button
              className="btn-download-pdf"
              onClick={handleDownloadPdf}
              disabled={!reportData || isPdfLoading}
            >
              {isPdfLoading ? (
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              ) : null}
              Download PDF
            </button>
          </div>
          <div className="selector-item selector-button">
            <button
              className="btn-download-pdf"
              onClick={handleOpenBulkDownload}
              style={{ background: '#072040' }}
            >
              <i className="bi bi-file-earmark-zip me-1"></i>
              Bulk Download
            </button>
          </div>
          {/* Edit Button - Drummond users only */}
          {user?.is_drummond_user && reportData && (
            <div className="selector-item selector-button">
              <button
                className={`btn-edit-content ${isEditMode ? 'active' : ''}`}
                onClick={() => setIsEditMode(!isEditMode)}
              >
                {isEditMode ? 'Close Editor' : 'Edit Content'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading report data...</p>
        </div>
      )}

      {/* Report Content with Split View */}
      {reportData && !isLoading && (
        <div className={`report-wrapper ${isEditMode ? 'split-view' : ''}`}>
          <div className="report-content">
            <ResearchReportContent
              reportData={reportData}
              displayFundName={displayFundName}
              theme={theme}
              brandDomain={isPreviewMode ? previewDomain : undefined}
            />
          </div>

          {/* Edit Panel - Side panel for inline editing */}
          {isEditMode && (
            <div className="edit-panel">
              {/* Fund Name Override */}
              <div className="mb-3 pb-3 border-bottom">
                <label className="form-label fw-bold" style={{ fontSize: '0.85rem', color: '#072040' }}>
                  Fund Name Override
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={fundNameOverride}
                  onChange={(e) => setFundNameOverride(e.target.value)}
                  placeholder={reportData.fund_details?.fund_name}
                  style={{ fontSize: '0.85rem' }}
                />
                <small className="text-muted">Leave blank to use original name</small>
              </div>
              <InlineContentEditor
                apirCode={selectedFund}
                fundName={displayFundName}
                onSave={handleContentSave}
                onClose={() => setIsEditMode(false)}
              />
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!reportData && !isLoading && selectedAssetClass && (
        <div className="empty-state">
          <p>Select a fund to view the research report</p>
        </div>
      )}

      {!selectedAssetClass && (
        <div className="empty-state">
          <p>Select an asset class to get started</p>
        </div>
      )}
      {/* Disclaimer Card */}
      <div className="card disclaimer-card mt-3">
        <div className="card-body">
          <div className="disclaimer-text">
            <ManagedDisclaimer dkey="data_notice" as="p" className="small text-muted mb-0" />
          </div>
        </div>
      </div>

      <BulkReportDownloadModal
        isOpen={isBulkModalOpen}
        onClose={handleCloseBulkModal}
        isGenerating={isBulkGenerating}
        jobProgress={bulkJobProgress}
        jobStatus={bulkJobStatus}
        message={bulkMessage}
        onGenerate={handleBulkGenerate}
      />
    </div>
  );
};

export default ResearchReportsApp;
